import { buildSettingsHashHref, resolveSettingsHash } from "@/app/paths";
import {
  buildAuthCallbackURL,
  normalizeAuthReturnPath,
} from "@/lib/auth/return-path";
import { SETTINGS_SECTION } from "@/lib/settings/sections";

const INTERNAL_URL_BASE = "https://sozo.invalid";
const MAZRICA_OAUTH_PROVIDER_ACCESS_DENIED = "access_denied";

export const MAZRICA_OAUTH_CALLBACK_STATUS = {
  CANCELLED: "cancelled",
  FAILED: "failed",
  REJECTED: "rejected",
  NO_DOMAIN: "no_domain",
  NEEDS_REAUTH: "needs_reauth",
  TEMPORARILY_UNAVAILABLE: "temporarily_unavailable",
} as const;

export type MazricaOAuthCallbackStatus =
  (typeof MAZRICA_OAUTH_CALLBACK_STATUS)[keyof typeof MAZRICA_OAUTH_CALLBACK_STATUS];

const MAZRICA_OAUTH_RESULT_CALLBACK_STATUSES = [
  MAZRICA_OAUTH_CALLBACK_STATUS.CANCELLED,
  MAZRICA_OAUTH_CALLBACK_STATUS.FAILED,
  MAZRICA_OAUTH_CALLBACK_STATUS.REJECTED,
] as const;

export type MazricaOAuthResultCallbackStatus =
  (typeof MAZRICA_OAUTH_RESULT_CALLBACK_STATUSES)[number];

export const MAZRICA_OAUTH_CALLBACK_QUERY_PARAM = "mazrica_status";

export const MAZRICA_OAUTH_ERROR_CALLBACK_PATH = "/auth/login/mazrica-error";

export const MAZRICA_SALES_CONNECTION_COMPLETE_PATH =
  "/api/oauth/mazrica/complete";

export const MAZRICA_SALES_CONNECTION_REQUEST_NONCE_PARAM =
  "mazrica_connection_nonce";

export const MAZRICA_SALES_CONNECTION_REQUEST_COOKIE =
  "sozo_mazrica_sales_connection_request";
export const MAZRICA_SALES_CONNECTION_REQUEST_COOKIE_PATH =
  MAZRICA_SALES_CONNECTION_COMPLETE_PATH;
export const MAZRICA_SALES_CONNECTION_REQUEST_COOKIE_MAX_AGE_SECONDS = 10 * 60;

const MAZRICA_SALES_CONNECTION_REQUEST_NONCE_PATTERN = /^[A-Za-z0-9_-]{1,256}$/;

const MAZRICA_SALES_SETTINGS_RETURN_PATH = buildSettingsHashHref({
  section: SETTINGS_SECTION.INTEGRATIONS,
});

export const MAZRICA_SALES_CONNECTION_COMPLETE_FALLBACK =
  MAZRICA_SALES_SETTINGS_RETURN_PATH;

function toInternalPath(url: URL) {
  return `${url.pathname}${url.search}${url.hash}`;
}

function parseInternalReturnPath(returnTo: string | null | undefined) {
  const normalized = normalizeAuthReturnPath(
    returnTo,
    MAZRICA_SALES_SETTINGS_RETURN_PATH,
  );

  try {
    return new URL(normalized, INTERNAL_URL_BASE);
  } catch {
    return new URL(MAZRICA_SALES_SETTINGS_RETURN_PATH, INTERNAL_URL_BASE);
  }
}

function isOAuthCallbackStatus(
  value: string,
): value is MazricaOAuthCallbackStatus {
  return Object.values(MAZRICA_OAUTH_CALLBACK_STATUS).some(
    (status) => status === value,
  );
}

export function parseMazricaOAuthCallbackStatus(
  value: string | null,
): MazricaOAuthCallbackStatus | null {
  return value !== null && isOAuthCallbackStatus(value) ? value : null;
}

export function isMazricaOAuthResultCallbackStatus(
  value: MazricaOAuthCallbackStatus,
): value is MazricaOAuthResultCallbackStatus {
  return MAZRICA_OAUTH_RESULT_CALLBACK_STATUSES.some(
    (status) => status === value,
  );
}

export function classifyMazricaOAuthProviderError(
  providerError: string,
): MazricaOAuthResultCallbackStatus {
  return providerError === MAZRICA_OAUTH_PROVIDER_ACCESS_DENIED
    ? MAZRICA_OAUTH_CALLBACK_STATUS.CANCELLED
    : MAZRICA_OAUTH_CALLBACK_STATUS.FAILED;
}

/**
 * Settings-initiated complete may tell the Mazrica Sales row why connection
 * was not created. Only these fixed values are placed in the URL.
 */
export function classifyMazricaSalesCompleteCallbackStatus(
  availabilityStatus: string,
): MazricaOAuthCallbackStatus | null {
  if (availabilityStatus === "connected") {
    return null;
  }
  if (
    availabilityStatus === MAZRICA_OAUTH_CALLBACK_STATUS.NO_DOMAIN ||
    availabilityStatus === MAZRICA_OAUTH_CALLBACK_STATUS.NEEDS_REAUTH ||
    availabilityStatus === MAZRICA_OAUTH_CALLBACK_STATUS.TEMPORARILY_UNAVAILABLE
  ) {
    return availabilityStatus;
  }
  return MAZRICA_OAUTH_CALLBACK_STATUS.REJECTED;
}

export function isMazricaSalesSettingsReturnPath(
  returnTo: string | null | undefined,
) {
  const normalized = normalizeAuthReturnPath(returnTo);
  if (normalized !== returnTo) {
    return false;
  }

  try {
    const parsed = new URL(normalized, INTERNAL_URL_BASE);
    if (parsed.pathname !== "/") {
      return false;
    }
    const resolved = resolveSettingsHash(parsed.hash);
    return (
      resolved.isSettingsHash &&
      resolved.section === SETTINGS_SECTION.INTEGRATIONS
    );
  } catch {
    return false;
  }
}

export function buildMazricaSalesLoginHref(
  returnTo: string,
  nonce?: string | null,
) {
  const normalizedReturnTo = normalizeAuthReturnPath(
    returnTo,
    MAZRICA_SALES_SETTINGS_RETURN_PATH,
  );
  const href = `/auth/login?returnTo=${encodeURIComponent(normalizedReturnTo)}`;
  if (!isMazricaSalesConnectionRequestNonce(nonce)) {
    return href;
  }
  return `${href}&${MAZRICA_SALES_CONNECTION_REQUEST_NONCE_PARAM}=${encodeURIComponent(nonce)}`;
}

export function buildMazricaOAuthErrorCallbackURL(
  returnTo: string | null | undefined,
  origin: string,
) {
  const normalizedReturnTo = normalizeAuthReturnPath(
    returnTo,
    MAZRICA_SALES_SETTINGS_RETURN_PATH,
  );
  const errorCallbackPath = `${MAZRICA_OAUTH_ERROR_CALLBACK_PATH}?returnTo=${encodeURIComponent(normalizedReturnTo)}`;
  return buildAuthCallbackURL(errorCallbackPath, origin);
}

export function isMazricaSalesConnectionRequestNonce(
  value: unknown,
): value is string {
  return (
    typeof value === "string" &&
    MAZRICA_SALES_CONNECTION_REQUEST_NONCE_PATTERN.test(value)
  );
}

export function buildMazricaLoginCallbackUrls(
  returnTo: string | null | undefined,
  origin: string,
  nonce?: string | null,
) {
  if (!isMazricaSalesSettingsReturnPath(returnTo)) {
    return { callbackURL: buildAuthCallbackURL(returnTo, origin) };
  }
  if (!isMazricaSalesConnectionRequestNonce(nonce)) {
    return { callbackURL: buildAuthCallbackURL(returnTo, origin) };
  }

  const normalizedReturnTo = normalizeAuthReturnPath(
    returnTo,
    MAZRICA_SALES_SETTINGS_RETURN_PATH,
  );
  const completePath =
    `${MAZRICA_SALES_CONNECTION_COMPLETE_PATH}?returnTo=${encodeURIComponent(normalizedReturnTo)}` +
    `&${MAZRICA_SALES_CONNECTION_REQUEST_NONCE_PARAM}=${encodeURIComponent(nonce)}`;
  return {
    callbackURL: buildAuthCallbackURL(completePath, origin),
    errorCallbackURL: buildMazricaOAuthErrorCallbackURL(returnTo, origin),
  };
}

export function buildMazricaSalesConnectionCallbackPath(
  returnTo: string | null | undefined,
  status: MazricaOAuthCallbackStatus,
) {
  const destination = parseInternalReturnPath(
    isMazricaSalesSettingsReturnPath(returnTo)
      ? returnTo
      : MAZRICA_SALES_SETTINGS_RETURN_PATH,
  );
  destination.searchParams.set(MAZRICA_OAUTH_CALLBACK_QUERY_PARAM, status);
  return toInternalPath(destination);
}

export function buildMazricaOAuthErrorReturnPath(args: {
  returnTo: string | null | undefined;
  providerError: string;
}) {
  if (!isMazricaSalesSettingsReturnPath(args.returnTo)) {
    return normalizeAuthReturnPath(args.returnTo);
  }

  return buildMazricaSalesConnectionCallbackPath(
    args.returnTo,
    classifyMazricaOAuthProviderError(args.providerError),
  );
}
